import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : XMenu can dispatch to different backends (semantic-ui and contexify)
// =============================================================================
// ---Third-party imports
import React, { useRef } from 'react';
import { Menu, Icon, Label, Dropdown, Button, Input, Popup, Header, Modal } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { isObject } from 'ldash';
import { useEffect, useState } from 'uix/hooks';
import { VIcon } from 'uix/graphics';
// ---Local imports
import { XMenu } from './menu';
import { KeyboardShortCut } from './keyboardShortcuts';
import { Link } from './links';
import styles from './contextMenu.scss';
export function contextFromEvent(e) {
    const left = e.clientX;
    const top = e.clientY;
    const right = left + 1;
    const bottom = top + 1;
    return {
        getBoundingClientRect: ()=>({
                left,
                top,
                right,
                bottom,
                height: 0,
                width: 0
            })
    };
}
const Group = ({ animation, header, style, children, setOpen, setPopup, labelSize, headerLinkProps })=>{
    let headerEl = header;
    if (isObject(header)) {
        headerEl = /*#__PURE__*/ _jsx(Label, {
            size: labelSize,
            style: style,
            basic: true,
            title: header.title,
            className: styles.labelHeader,
            icon: header.icon,
            content: header.value
        });
    }
    if (isObject(headerLinkProps)) {
        headerEl = /*#__PURE__*/ _jsx(Link, {
            ...headerLinkProps,
            children: headerEl
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            headerEl,
            children && /*#__PURE__*/ _jsx("div", {
                className: styles.group,
                children: React.Children.map(children, (child)=>/*#__PURE__*/ React.cloneElement(child, {
                        animation: animation,
                        setOpen: setOpen,
                        setPopup: setPopup
                    }))
            })
        ]
    });
};
const Item = ({ animation, children, icon, name, header, options, setPopup, setOpen, onClick, ...props })=>{
    const onClickHandle = (param)=>{
        param && param();
        setOpen ? setOpen(false) : setPopup ? setPopup(false) : null;
    };
    if (options) {
        return /*#__PURE__*/ _jsx(Dropdown, {
            item: true,
            trigger: /*#__PURE__*/ _jsxs("span", {
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: icon,
                        style: {
                            marginRight: '0.5rem'
                        }
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: name
                    })
                ]
            }),
            ...props,
            children: /*#__PURE__*/ _jsxs(Dropdown.Menu, {
                className: `transition ${animation} ${open ? 'in' : 'out'}`,
                style: {
                    borderTop: 'none'
                },
                children: [
                    header && /*#__PURE__*/ _jsx(Dropdown.Header, {
                        icon: header.icon,
                        content: header.value
                    }),
                    options && /*#__PURE__*/ _jsx(Dropdown.Menu, {
                        scrolling: true,
                        children: options.map((opt)=>{
                            let { func, value, ...o } = opt;
                            return /*#__PURE__*/ _jsx(Dropdown.Item, {
                                text: value,
                                onClick: ()=>onClickHandle(func),
                                ...o
                            }, value);
                        })
                    })
                ]
            })
        });
    } else {
        return /*#__PURE__*/ _jsxs(XMenu.Item, {
            style: {
                textTransform: 'none'
            },
            onClick: ()=>onClickHandle(onClick),
            ...props,
            children: [
                /*#__PURE__*/ _jsx(VIcon, {
                    name: icon
                }),
                name
            ]
        });
    }
};
export const ContextMenu = ({ animation = 'slide down', children, content, context = undefined, delay = false, disableClick, dropdown = false, header, setOpen = undefined, open: open1 = undefined, options, quickOptions, rightClick = false, closeOnEscape = true, size = null, trigger })=>{
    const [timeout, setTimeoutHandle] = useState(delay);
    // this is ther inner onOpen tracking
    const [popup, setPopup] = useState(false);
    const ref = useRef(null);
    if (delay === true) {
        //default mouseHover delay
        delay = '1800';
    }
    const setOpenState = (value)=>{
        setOpen ? setOpen(value) : setPopup(value);
    };
    let useOutsideClick = (ref)=>useEffect(()=>{
            let handleClickOutside = (event)=>{
                if (ref.current && !ref.current.contains(event.target)) {
                    setOpenState(false);
                }
            };
            // Bind the event listener
            document.addEventListener('mousedown', handleClickOutside);
            return ()=>{
                // Unbind the event listener on clean up
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [
            ref
        ]);
    useOutsideClick(ref);
    const handleMouseEnter = ()=>{
        if (delay) {
            setTimeoutHandle(setTimeout(()=>{
                setOpenState(true);
            }, delay));
        }
    };
    const handleMouseLeave = ()=>{
        clearTimeout(timeout);
    };
    const handleClick = (e)=>{
        e.preventDefault();
        if (e.type === 'contextmenu' && rightClick) {
            setOpenState(true);
        }
        if (e.type === 'click' && rightClick) {
            handleClose();
        } else {
            if (e.type === 'click') {
                setOpenState(true);
            }
        }
    };
    const handleClose = ()=>{
        setOpenState(false);
    };
    const onClickHandle = (param)=>{
        param && param();
        handleClose();
    };
    let labelSize = {
        mini: 'mini',
        tiny: 'mini',
        small: 'tiny',
        default: 'small',
        large: 'default'
    }[size];
    let quickbar = {
        display: 'flex',
        position: 'relative',
        margin: '0',
        right: '0',
        left: '0',
        top: '0'
    };
    let animationClass = null;
    if (animation) {
        animationClass = `transition ${animation} ${open1 ? 'in' : 'out' || popup ? 'in' : 'out'}`;
    }
    if (dropdown) {
        return /*#__PURE__*/ _jsx("div", {
            className: styles.contextMenu,
            ref: ref,
            children: /*#__PURE__*/ _jsx(Dropdown, {
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
                onClick: disableClick ? null : handleClick,
                onClose: handleClose,
                onContextMenu: handleClick,
                trigger: trigger,
                icon: false,
                open: popup,
                children: /*#__PURE__*/ _jsxs(Dropdown.Menu, {
                    children: [
                        quickOptions && /*#__PURE__*/ _jsx("div", {
                            className: clsx('menu', styles.quickbar),
                            style: quickbar,
                            children: quickOptions.map((opt)=>{
                                let { func, value, ...o } = opt;
                                return /*#__PURE__*/ _jsx(Dropdown.Item, {
                                    onClick: ()=>onClickHandle(func),
                                    ...o
                                }, value);
                            })
                        }),
                        header && /*#__PURE__*/ _jsx(Dropdown.Header, {
                            icon: header.icon,
                            content: header.value
                        }),
                        options && /*#__PURE__*/ _jsx(Dropdown.Menu, {
                            scrolling: true,
                            children: options.map((opt)=>{
                                let { func, value, ...o } = opt;
                                return /*#__PURE__*/ _jsx(Dropdown.Item, {
                                    text: value,
                                    onClick: ()=>onClickHandle(func),
                                    ...o
                                }, value);
                            })
                        })
                    ]
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Popup, {
        open: open1 || popup,
        context: context,
        className: clsx(styles.contextMenu, styles.popup, animationClass),
        basic: true,
        hideOnScroll: context ? true : true,
        style: {
            padding: ' 0'
        },
        trigger: /*#__PURE__*/ _jsx("div", {
            style: {
                maxWidth: 'fit-content'
            },
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            onContextMenu: handleClick,
            onClick: disableClick ? null : handleClick,
            as: "button",
            children: trigger
        }),
        children: [
            /*#__PURE__*/ _jsx(KeyboardShortCut, {
                name: "Escape",
                onTrigger: (e)=>setOpenState(false)
            }),
            /*#__PURE__*/ _jsx(Popup.Content, {
                onClick: (e)=>e.stopPropagation(),
                children: /*#__PURE__*/ _jsxs("div", {
                    ref: ref,
                    children: [
                        quickOptions && /*#__PURE__*/ _jsx(XMenu, {
                            size: size,
                            borderless: true,
                            className: styles.quickbar,
                            style: quickbar,
                            children: quickOptions.map((opt)=>{
                                let { func, value, ...o } = opt;
                                return /*#__PURE__*/ _jsx(XMenu.Item, {
                                    onClick: ()=>onClickHandle(func),
                                    ...o
                                }, value);
                            })
                        }),
                        header && /*#__PURE__*/ _jsx(Label, {
                            size: labelSize,
                            style: {
                                textTransform: 'uppercase',
                                border: '0px'
                            },
                            basic: true,
                            className: styles.labelHeader,
                            icon: header.icon,
                            content: header.value
                        }),
                        options && /*#__PURE__*/ _jsx(XMenu, {
                            style: {
                                marginTop: '0'
                            },
                            borderless: true,
                            size: size,
                            vertical: true,
                            children: options.map((opt)=>{
                                let { func, value, ...o } = opt;
                                return /*#__PURE__*/ _jsx(XMenu.Item, {
                                    content: value,
                                    onClick: ()=>onClickHandle(func),
                                    ...o
                                }, value);
                            })
                        }),
                        content && content,
                        children && /*#__PURE__*/ _jsx(XMenu, {
                            style: {
                                marginTop: '0'
                            },
                            borderless: true,
                            size: size,
                            vertical: true,
                            children: React.Children.map(children, (child)=>/*#__PURE__*/ React.cloneElement(child, {
                                    setPopup: setPopup,
                                    setOpen: setOpen,
                                    animation: animation,
                                    labelSize: labelSize
                                }))
                        })
                    ]
                })
            })
        ]
    });
};
// ---Exports
ContextMenu.Item = Item;
ContextMenu.Group = Group;
